import React, { useContext } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import EmbedContext from "../../context/Embed";
import { C, FONT_BODY } from "../../utils/designTokens";
// Catering now shares the site red, so its pill sits beside the partner one
// in the same tint: catering is told apart by its cloche.

const pillSx = (compact) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  height: 40,
  px: compact ? 1.25 : 1.75,
  borderRadius: "24px",
  fontWeight: 600,
  fontSize: "0.875rem",
  lineHeight: 1,
  textDecoration: "none",
  whiteSpace: "nowrap",
  flexShrink: 0,
  transition: "background-color .15s ease, color .15s ease",
});

/**
 * The header's call to action. On the home page it is "Become a partner", so
 * restaurants and stores find the merchant sign-up (/partner-signup); on every
 * other page it is Catering (backend migration 131). Neither in embed mode (a
 * restaurant's own ordering page) nor on the sign-up pages themselves.
 * compact: the icon alone, where the header is crowded.
 */
function BecomePartnerLink({ compact = false }) {
  const { isEmbed } = useContext(EmbedContext);
  const location = useLocation();
  if (isEmbed || location.pathname.startsWith("/partner-signup")) return null;
  if (location.pathname !== "/") {
    return <CateringLink compact={compact} active={location.pathname.startsWith("/catering")} />;
  }

  const link = (
    <Box
      component={RouterLink}
      to="/partner-signup"
      aria-label={compact ? "Become a partner: sign up your restaurant or store" : undefined}
      sx={{
        ...pillSx(compact),
        backgroundColor: "#FFF1EE",
        color: "#CC0000",
        "&:hover": { backgroundColor: "#FFE2DA" },
        "&:focus-visible": { outline: "2px solid #CC0000", outlineOffset: 2 },
      }}
    >
      <StorefrontOutlinedIcon sx={{ fontSize: 20 }} />
      {!compact && <span>Become a partner</span>}
    </Box>
  );
  return compact ? <Tooltip title="Become a partner">{link}</Tooltip> : link;
}

/** Catering, in its own accent; outlined while you are on the Catering page. */
function CateringLink({ compact, active }) {
  const link = (
    <Box
      component={RouterLink}
      to="/catering"
      aria-label={compact ? "Catering" : undefined}
      aria-current={active ? "page" : undefined}
      sx={{
        ...pillSx(compact),
        height: 44,
        px: compact ? 1.375 : 2,
        fontFamily: FONT_BODY,
        backgroundColor: C.redSoft,
        color: C.red,
        boxShadow: active ? `inset 0 0 0 1.5px ${C.red}` : "none",
        "&:hover": { backgroundColor: "#F6DCD8" },
        "&:focus-visible": { outline: `2px solid ${C.ink}`, outlineOffset: 2 },
      }}
    >
      <RoomServiceRoundedIcon sx={{ fontSize: 20 }} />
      {!compact && <span>Catering</span>}
    </Box>
  );
  return compact ? <Tooltip title="Catering">{link}</Tooltip> : link;
}

export default React.memo(BecomePartnerLink);
